// ─────────────────────────────────────────────────────────────────────────────
// SXT BODY PROXY — collision body for cloth physics
// ─────────────────────────────────────────────────────────────────────────────
// Problem: Ammo.js cloth doesn't know the body exists. Without collision
// geometry the garment clips straight through the person.
//
// Solution: 14 Ammo btCapsuleShape + btSphereShape rigid bodies arranged
// in body topology, driven by MediaPipe landmarks each frame.
// The cloth soft body collides against these — garment drapes over the body.
//
// No SMPL license needed. No external model. Pure geometry derived from
// the landmark positions + estimated body measurements.
//
// Collision shapes:
//   Head       1 sphere
//   Neck       1 capsule
//   Torso      3 capsules  (upper / mid / lower)
//   L/R Arm    2 capsules each  (upper arm + forearm)
//   L/R Leg    2 capsules each  (thigh + shin)
//   Total: 14 shapes, ~50 vertices of collision, 60fps viable
// ─────────────────────────────────────────────────────────────────────────────

import * as THREE from "three";
import type { NormalizedLandmark } from "./pose";

// ── Landmark indices we use ───────────────────────────────────────────────────
const MP = {
  NOSE:           0,
  L_SHOULDER:    11,  R_SHOULDER:    12,
  L_ELBOW:       13,  R_ELBOW:       14,
  L_WRIST:       15,  R_WRIST:       16,
  L_HIP:         23,  R_HIP:         24,
  L_KNEE:        25,  R_KNEE:        26,
  L_ANKLE:       27,  R_ANKLE:       28,
} as const;

// ── Shape descriptor (built once, updated per frame) ─────────────────────────
export interface ProxyShape {
  id:     string;
  type:   "capsule" | "sphere";
  center: THREE.Vector3;  // world-space center this frame
  up:     THREE.Vector3;  // capsule axis direction
  radius: number;         // metres
  height: number;         // capsule segment length (0 for sphere)
}

export interface BodyProxy {
  shapes:     ProxyShape[];
  shoulderW:  number;  // estimated shoulder width (metres)
  torsoH:     number;  // estimated torso height  (metres)
  scale:      number;  // body scale factor
}

// ── Convert normalised MediaPipe landmark → world-space Vector3 ───────────────
// Uses same formula as RetargetEngine: x=(lm.x-0.5)*4, y=-(lm.y-0.5)*4, z=lm.z*2
function toWorld(
  lm: NormalizedLandmark,
  isMirrored: boolean
): THREE.Vector3 {
  const x = (isMirrored ? 1 - lm.x : lm.x);
  return new THREE.Vector3(
    (x    - 0.5) * 4,
    -(lm.y - 0.5) * 4,
    lm.z  * -2
  );
}

function midpoint(a: THREE.Vector3, b: THREE.Vector3): THREE.Vector3 {
  return a.clone().add(b).multiplyScalar(0.5);
}

function direction(from: THREE.Vector3, to: THREE.Vector3): THREE.Vector3 {
  const d = to.clone().sub(from);
  const len = d.length();
  return len > 0.001 ? d.divideScalar(len) : new THREE.Vector3(0, 1, 0);
}

// ── Build body proxy from current landmarks ───────────────────────────────────
export function buildBodyProxy(
  lms: NormalizedLandmark[],
  isMirrored: boolean
): BodyProxy | null {
  // Need at minimum: shoulders, hips
  const required = [MP.L_SHOULDER, MP.R_SHOULDER, MP.L_HIP, MP.R_HIP];
  for (const i of required) {
    if (!lms[i] || (lms[i].visibility ?? 1) < 0.3) return null;
  }

  // ── World positions ────────────────────────────────────────────────────────
  const lSh  = toWorld(lms[MP.L_SHOULDER], isMirrored);
  const rSh  = toWorld(lms[MP.R_SHOULDER], isMirrored);
  const lHip = toWorld(lms[MP.L_HIP],      isMirrored);
  const rHip = toWorld(lms[MP.R_HIP],      isMirrored);

  const lEl  = lms[MP.L_ELBOW]?.visibility ?? 0 > 0.3
    ? toWorld(lms[MP.L_ELBOW],  isMirrored) : null;
  const rEl  = lms[MP.R_ELBOW]?.visibility ?? 0 > 0.3
    ? toWorld(lms[MP.R_ELBOW],  isMirrored) : null;
  const lWr  = lms[MP.L_WRIST]?.visibility ?? 0 > 0.3
    ? toWorld(lms[MP.L_WRIST],  isMirrored) : null;
  const rWr  = lms[MP.R_WRIST]?.visibility ?? 0 > 0.3
    ? toWorld(lms[MP.R_WRIST],  isMirrored) : null;
  const lKn  = lms[MP.L_KNEE]?.visibility  ?? 0 > 0.3
    ? toWorld(lms[MP.L_KNEE],   isMirrored) : null;
  const rKn  = lms[MP.R_KNEE]?.visibility  ?? 0 > 0.3
    ? toWorld(lms[MP.R_KNEE],   isMirrored) : null;
  const lAn  = lms[MP.L_ANKLE]?.visibility ?? 0 > 0.3
    ? toWorld(lms[MP.L_ANKLE],  isMirrored) : null;
  const rAn  = lms[MP.R_ANKLE]?.visibility ?? 0 > 0.3
    ? toWorld(lms[MP.R_ANKLE],  isMirrored) : null;
  const nose = lms[MP.NOSE]?.visibility ?? 0 > 0.3
    ? toWorld(lms[MP.NOSE],     isMirrored) : null;

  // ── Measurements ──────────────────────────────────────────────────────────
  const shoulderW = lSh.distanceTo(rSh);           // shoulder span
  const hipW      = lHip.distanceTo(rHip);          // hip span
  const torsoH    = lSh.clone().add(rSh).multiplyScalar(0.5)
                       .distanceTo(lHip.clone().add(rHip).multiplyScalar(0.5));
  const scale     = Math.max(0.3, Math.min(2.0, shoulderW / 0.45)); // norm to ~45cm

  // Radii proportional to body scale
  const rTorsoU  = shoulderW * 0.18;  // upper torso  (chest)
  const rTorsoM  = shoulderW * 0.16;  // mid torso
  const rTorsoL  = hipW      * 0.18;  // lower torso  (hips)
  const rArm     = shoulderW * 0.065; // arm tube
  const rLeg     = hipW      * 0.10;  // leg tube
  const rHead    = shoulderW * 0.20;  // head sphere

  // Derived positions
  const shCenter  = midpoint(lSh, rSh);
  const hipCenter = midpoint(lHip, rHip);
  const up        = new THREE.Vector3(0, 1, 0);

  // Upper torso: between shoulders and 1/3 down to hips
  const torsoU_bot = shCenter.clone().lerp(hipCenter, 0.33);
  const torsoU_ctr = midpoint(shCenter, torsoU_bot);
  const torsoU_h   = shCenter.distanceTo(torsoU_bot);

  // Mid torso: 1/3 to 2/3
  const torsoM_bot = shCenter.clone().lerp(hipCenter, 0.66);
  const torsoM_ctr = midpoint(torsoU_bot, torsoM_bot);
  const torsoM_h   = torsoU_bot.distanceTo(torsoM_bot);

  // Lower torso: 2/3 to hips
  const torsoL_ctr = midpoint(torsoM_bot, hipCenter);
  const torsoL_h   = torsoM_bot.distanceTo(hipCenter);

  // Neck + head
  const headOffset = up.clone().multiplyScalar(shoulderW * 0.45);
  const neckPos    = nose
    ? midpoint(shCenter, nose).add(up.clone().multiplyScalar(-rHead * 0.5))
    : shCenter.clone().add(headOffset.clone().multiplyScalar(0.6));
  const headPos    = nose
    ? nose.clone()
    : shCenter.clone().add(headOffset);

  const shapes: ProxyShape[] = [];

  // ── Head ──────────────────────────────────────────────────────────────────
  shapes.push({
    id: "head", type: "sphere",
    center: headPos, up,
    radius: rHead, height: 0,
  });

  // ── Neck ──────────────────────────────────────────────────────────────────
  shapes.push({
    id: "neck", type: "capsule",
    center: neckPos, up,
    radius: rHead * 0.38, height: rHead * 0.5,
  });

  // ── Torso (3 segments) ────────────────────────────────────────────────────
  shapes.push({
    id: "torso_upper", type: "capsule",
    center: torsoU_ctr, up,
    radius: rTorsoU, height: torsoU_h,
  });
  shapes.push({
    id: "torso_mid", type: "capsule",
    center: torsoM_ctr, up,
    radius: rTorsoM, height: torsoM_h,
  });
  shapes.push({
    id: "torso_lower", type: "capsule",
    center: torsoL_ctr, up,
    radius: rTorsoL, height: torsoL_h,
  });

  // ── Arms ─────────────────────────────────────────────────────────────────
  // Left upper arm: shoulder → elbow
  if (lEl) {
    shapes.push({
      id: "l_upper_arm", type: "capsule",
      center: midpoint(lSh, lEl),
      up:     direction(lSh, lEl),
      radius: rArm,
      height: lSh.distanceTo(lEl),
    });
  }
  // Left forearm: elbow → wrist
  if (lEl && lWr) {
    shapes.push({
      id: "l_forearm", type: "capsule",
      center: midpoint(lEl, lWr),
      up:     direction(lEl, lWr),
      radius: rArm * 0.85,
      height: lEl.distanceTo(lWr),
    });
  }

  // Right upper arm
  if (rEl) {
    shapes.push({
      id: "r_upper_arm", type: "capsule",
      center: midpoint(rSh, rEl),
      up:     direction(rSh, rEl),
      radius: rArm,
      height: rSh.distanceTo(rEl),
    });
  }
  // Right forearm
  if (rEl && rWr) {
    shapes.push({
      id: "r_forearm", type: "capsule",
      center: midpoint(rEl, rWr),
      up:     direction(rEl, rWr),
      radius: rArm * 0.85,
      height: rEl.distanceTo(rWr),
    });
  }

  // ── Legs ─────────────────────────────────────────────────────────────────
  // Left thigh: hip → knee
  if (lKn) {
    shapes.push({
      id: "l_thigh", type: "capsule",
      center: midpoint(lHip, lKn),
      up:     direction(lHip, lKn),
      radius: rLeg,
      height: lHip.distanceTo(lKn),
    });
  }
  // Left shin: knee → ankle
  if (lKn && lAn) {
    shapes.push({
      id: "l_shin", type: "capsule",
      center: midpoint(lKn, lAn),
      up:     direction(lKn, lAn),
      radius: rLeg * 0.8,
      height: lKn.distanceTo(lAn),
    });
  }

  // Right thigh
  if (rKn) {
    shapes.push({
      id: "r_thigh", type: "capsule",
      center: midpoint(rHip, rKn),
      up:     direction(rHip, rKn),
      radius: rLeg,
      height: rHip.distanceTo(rKn),
    });
  }
  // Right shin
  if (rKn && rAn) {
    shapes.push({
      id: "r_shin", type: "capsule",
      center: midpoint(rKn, rAn),
      up:     direction(rKn, rAn),
      radius: rLeg * 0.8,
      height: rKn.distanceTo(rAn),
    });
  }

  return { shapes, shoulderW, torsoH, scale };
}

// ── Ammo.js collision body builder ───────────────────────────────────────────
// Creates/updates btRigidBody objects in the Ammo world, one per proxy shape.
// These are kinematic (mass=0, moved via setWorldTransform each frame).
// The Ammo soft body cloth collides against them automatically.

export class BodyCollider {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private ammo: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private world: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private bodies: Map<string, any> = new Map();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private transforms: Map<string, any> = new Map();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(ammo: any, world: any) {
    this.ammo  = ammo;
    this.world = world;
  }

  // ── Build or update collision bodies for a proxy ─────────────────────────
  sync(proxy: BodyProxy) {
    for (const shape of proxy.shapes) {
      if (this.bodies.has(shape.id)) {
        // Update existing body position
        this._moveBody(shape.id, shape.center, shape.up);
      } else {
        // Create new body
        this._createBody(shape);
      }
    }
    // Remove bodies for shapes no longer in proxy (limbs that became invisible)
    for (const id of this.bodies.keys()) {
      if (!proxy.shapes.find(s => s.id === id)) {
        this._removeBody(id);
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _createBody(shape: ProxyShape): any {
    const A = this.ammo;

    // Build collision shape
    let colShape;
    if (shape.type === "sphere") {
      colShape = new A.btSphereShape(shape.radius);
    } else {
      // btCapsuleShape: radius, height (segment between sphere centres)
      // Axis: Y by default (capsule stands upright)
      const segLen = Math.max(0.001, shape.height - shape.radius * 2);
      colShape = new A.btCapsuleShape(shape.radius, segLen);
    }

    // Build transform
    const transform = new A.btTransform();
    transform.setIdentity();

    const origin = new A.btVector3(shape.center.x, shape.center.y, shape.center.z);
    transform.setOrigin(origin);
    A.destroy(origin);

    // Rotation: align Y axis to shape.up
    const quat = this._quatFromUp(shape.up);
    const bQuat = new A.btQuaternion(quat.x, quat.y, quat.z, quat.w);
    transform.setRotation(bQuat);
    A.destroy(bQuat);

    const motionState = new A.btDefaultMotionState(transform);
    const rbInfo = new A.btRigidBodyConstructionInfo(0, motionState, colShape);
    const body   = new A.btRigidBody(rbInfo);
    A.destroy(rbInfo);

    // Kinematic flags: CF_KINEMATIC_OBJECT | CF_STATIC_OBJECT
    body.setCollisionFlags(body.getCollisionFlags() | 2 | 1);
    body.setActivationState(4); // DISABLE_DEACTIVATION

    this.world.addRigidBody(body);
    this.bodies.set(shape.id, body);
    this.transforms.set(shape.id, transform);

    return body;
  }

  private _moveBody(id: string, center: THREE.Vector3, up: THREE.Vector3) {
    const A     = this.ammo;
    const body  = this.bodies.get(id);
    const trans = this.transforms.get(id);
    if (!body || !trans) return;

    const origin = new A.btVector3(center.x, center.y, center.z);
    trans.setOrigin(origin);
    A.destroy(origin);

    const quat  = this._quatFromUp(up);
    const bQuat = new A.btQuaternion(quat.x, quat.y, quat.z, quat.w);
    trans.setRotation(bQuat);
    A.destroy(bQuat);

    body.getMotionState().setWorldTransform(trans);
    body.setWorldTransform(trans);
  }

  private _removeBody(id: string) {
    const body = this.bodies.get(id);
    if (body) {
      this.world.removeRigidBody(body);
      this.ammo.destroy(body);
      this.bodies.delete(id);
    }
    const trans = this.transforms.get(id);
    if (trans) {
      this.ammo.destroy(trans);
      this.transforms.delete(id);
    }
  }

  // Compute quaternion that rotates Y-axis to align with `up`
  private _quatFromUp(up: THREE.Vector3): THREE.Quaternion {
    const yAxis = new THREE.Vector3(0, 1, 0);
    const q = new THREE.Quaternion();
    q.setFromUnitVectors(yAxis, up.clone().normalize());
    return q;
  }

  dispose() {
    for (const id of [...this.bodies.keys()]) {
      this._removeBody(id);
    }
    this.bodies.clear();
    this.transforms.clear();
  }
}

// ── Debug visualiser (Three.js wireframe overlay) ────────────────────────────
// Call in dev mode to see the collision proxy shapes on screen.
export class BodyProxyDebug {
  private scene: THREE.Scene;
  private meshes: Map<string, THREE.Mesh> = new Map();

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  update(proxy: BodyProxy | null) {
    // Hide all first
    this.meshes.forEach(m => { m.visible = false; });

    if (!proxy) return;

    for (const shape of proxy.shapes) {
      let mesh = this.meshes.get(shape.id);

      if (!mesh) {
        const geo = shape.type === "sphere"
          ? new THREE.SphereGeometry(1, 8, 6)
          : new THREE.CapsuleGeometry(1, 1, 4, 8);
        const mat = new THREE.MeshBasicMaterial({
          color: 0x00ffff,
          wireframe: true,
          transparent: true,
          opacity: 0.3,
        });
        mesh = new THREE.Mesh(geo, mat);
        mesh.frustumCulled = false;
        this.scene.add(mesh);
        this.meshes.set(shape.id, mesh);
      }

      mesh.visible = true;
      mesh.position.copy(shape.center);

      // Scale geometry to match shape dimensions
      if (shape.type === "sphere") {
        mesh.scale.setScalar(shape.radius);
      } else {
        // CapsuleGeometry has radius=1, height=1 by default
        const segLen = Math.max(0.001, shape.height - shape.radius * 2);
        mesh.scale.set(shape.radius, shape.radius + segLen / 2, shape.radius);
      }

      // Align capsule to shape.up direction
      const yAxis = new THREE.Vector3(0, 1, 0);
      const q = new THREE.Quaternion();
      q.setFromUnitVectors(yAxis, shape.up.clone().normalize());
      mesh.quaternion.copy(q);
    }
  }

  dispose() {
    this.meshes.forEach(m => {
      this.scene.remove(m);
      (m.geometry as THREE.BufferGeometry).dispose();
      (m.material as THREE.Material).dispose();
    });
    this.meshes.clear();
  }
}
